/* HRC - Hercules Cart Notification */

(() => {
  const SELECTORS = {
    root: "#hrc-cart-notification",
    inner: ".hrc-cart-notification__inner",
    close: "#hrc-cart-notification-close",
    continue: "#hrc-cart-notification-continue",
    product: "#hrc-cart-notification-product",
    count: "#hrc-cart-notification-count",
  };

  const getRoutesRoot = () => {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  };

  const getCartJsUrl = () => `${getRoutesRoot()}cart.js`;

  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#39;";
        default:
          return ch;
      }
    });

  const formatMoneyFromCents = (cents) => {
    if (window.HrcFormatMoney) {
      return window.HrcFormatMoney(cents, { forceDecimals: true });
    }
    const abs = Math.round(Math.abs(Number(cents ?? 0)));
    const grouped = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${grouped},${String(abs % 100).padStart(2, "0")}TL`;
  };

  const getAddJsUrl = (form) => {
    const url = new URL(
      form.getAttribute("action") || `${getRoutesRoot()}cart/add`,
      window.location.origin,
    );
    if (url.pathname.endsWith("/cart/add")) url.pathname = `${url.pathname}.js`;
    if (url.pathname.endsWith("/cart/add/"))
      url.pathname = `${url.pathname.slice(0, -1)}.js`;
    return url.toString();
  };

  /** Shopify prices are cents. Block any free (0 TL) sell path. */
  const toCents = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : NaN;
  };

  const findVariantPriceCents = (variantId) => {
    const id = String(variantId || "");
    if (!id) return null;

    const scripts = document.querySelectorAll(
      'script[type="application/json"][id^="ProductsJson-"]',
    );
    for (const script of scripts) {
      try {
        const variants = JSON.parse(script.textContent || "[]");
        if (!Array.isArray(variants)) continue;
        const match = variants.find((v) => String(v?.id) === id);
        if (match && match.price != null) return toCents(match.price);
      } catch (_) {
        /* ignore */
      }
    }

    try {
      const safeId =
        typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
      const card = document.querySelector(
        `hrc-product-card[data-variant-id="${safeId}"]`,
      );
      if (card?.dataset?.price != null) return toCents(card.dataset.price);
    } catch (_) {
      /* ignore */
    }

    return null;
  };

  const GIFT_PROP = "_campaign_gift";

  const isZeroPriceVariant = (variantId) => {
    const price = findVariantPriceCents(variantId);
    return price === 0;
  };

  const hasGiftProperty = (item) => {
    if (typeof window.HrcGift?.hasGiftProperty === "function") {
      return window.HrcGift.hasGiftProperty(item);
    }
    const props = item?.properties || {};
    if (Array.isArray(props)) {
      return props.some(
        (entry) => entry?.name === GIFT_PROP || entry?.[0] === GIFT_PROP,
      );
    }
    return Boolean(props[GIFT_PROP] || props._hercules_gift);
  };

  const isCampaignGiftItem = (item) => {
    if (typeof window.HrcGift?.isGiftLine === "function") {
      return window.HrcGift.isGiftLine(item);
    }
    return hasGiftProperty(item);
  };

  const isGiftOnlyPayload = (item) => {
    if (typeof window.HrcGift?.isGiftOnlyProduct === "function") {
      return window.HrcGift.isGiftOnlyProduct(item);
    }
    return false;
  };

  const isUnauthorizedGiftItem = (item) => {
    if (typeof window.HrcGift?.isUnauthorizedGift === "function") {
      return window.HrcGift.isUnauthorizedGift(item);
    }
    return isGiftOnlyPayload(item) && !isCampaignGiftItem(item);
  };

  const isCampaignGiftPayload = (data) => {
    const item = data?.items?.[0] || data;
    return isCampaignGiftItem(item);
  };

  const campaignIsActive = () => {
    if (typeof window.HrcCampaign?.isCampaignActive === "function") {
      return window.HrcCampaign.isCampaignActive() === true;
    }
    return (
      document.body.classList.contains("hrc-campaign-active") ||
      document.documentElement.classList.contains("hrc-campaign-hydrated")
    );
  };

  const campaignGiftShouldShow = () => {
    const gift = window.hrcCampaignConfig?.gift;
    if (!gift || gift.enabled === false) return false;
    if (window.hrcIsB2B === true) return false;
    return campaignIsActive();
  };

  const campaignGiftFallbackItem = (lineItem) => {
    const gift = window.hrcCampaignConfig?.gift || {};
    const title =
      lineItem?.product_title ||
      lineItem?.title ||
      gift.title ||
      "Hercules Mouthguard";
    const image =
      lineItem?.featured_image?.url || lineItem?.image || gift.image || "";
    return {
      product_title: title,
      title,
      image,
      url: lineItem?.url || gift.url || "",
      featured_image:
        lineItem?.featured_image || (image ? { url: image, alt: title } : null),
      quantity: 1,
      original_price: 0,
      final_price: 0,
    };
  };

  function formTargetsZeroPrice() {
    return false;
  }

  window.HrcCartGuard = {
    isZeroPriceVariant,
    formTargetsZeroPrice,
    findVariantPriceCents,
    isCampaignGiftPayload,
    isCampaignGiftItem,
    isGiftOnlyPayload,
    isUnauthorizedGiftItem,
  };

  const fetchCart = async () => {
    const res = await fetch(getCartJsUrl(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return await res.json();
  };

  const formatBadgeText = (count) => {
    const n = Number(count ?? 0);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n > 99 ? "99+" : String(n);
  };

  const readCurrentCartCount = () => {
    const badge = document.querySelector(".navbar__cart-badge");
    if (!badge) return 0;
    const text = badge.textContent.trim();
    if (text === "99+") return 99;
    const parsed = parseInt(text, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const updateCartCountUI = (count) => {
    const n = Math.max(0, Number(count ?? 0) || 0);
    const badgeText = formatBadgeText(n);

    document.querySelectorAll(".navbar__cart").forEach((cartBtn) => {
      let badge = cartBtn.querySelector(".navbar__cart-badge");

      if (badgeText) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "navbar__cart-badge";
          badge.setAttribute("aria-hidden", "true");
          cartBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      } else if (badge) {
        badge.remove();
      }

      cartBtn.setAttribute(
        "aria-label",
        (window.hrcI18n?.cartAria || "Cart (__COUNT__ items)")
          .replace("__COUNT__", String(n))
          .replace("{{ count }}", String(n)),
      );
    });

    const countEl = document.querySelector(SELECTORS.count);
    if (countEl) {
      countEl.textContent = String(n);
    }
  };

  const syncCartFromServer = async () => {
    const cart = await fetchCart();
    updateCartCountUI(cart?.item_count);
    return cart;
  };

  class HrcCartNotification {
    constructor() {
      this.root = document.querySelector(SELECTORS.root);
      if (!this.root) return;

      this.inner = this.root.querySelector(SELECTORS.inner);
      this.closeBtn = this.root.querySelector(SELECTORS.close);
      this.continueBtn = this.root.querySelector(SELECTORS.continue);
      this.productEl = this.root.querySelector(SELECTORS.product);
      this.countEl = this.root.querySelector(SELECTORS.count);

      this.isOpen = false;
      this.lastActiveElement = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onBackdropClick = this.onBackdropClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onContinue = this.onContinue.bind(this);

      this.closeBtn?.addEventListener("click", this.onClose);
      this.continueBtn?.addEventListener("click", this.onContinue);
      this.root.addEventListener("mousedown", this.onBackdropClick);

      this.bindAddToCartInterception();
    }

    bindAddToCartInterception() {
      document.addEventListener(
        "submit",
        async (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;

          const action = (form.getAttribute("action") || "").toLowerCase();
          if (!action.includes("/cart/add")) return;

          e.preventDefault();
          e.stopPropagation();

          if (formTargetsZeroPrice(form)) {
            console.warn("[HRC] Blocked add-to-cart: 0 TL product.");
            return;
          }

          const formVariantId = form.querySelector('[name="id"]')?.value;
          if (
            form.getAttribute("data-gift-only") === "true" ||
            isGiftOnlyPayload({
              variant_id: formVariantId,
              id: formVariantId,
              product_id: form.getAttribute("data-product-id"),
            })
          ) {
            console.warn("[HRC] Blocked add-to-cart: gift-only product.");
            return;
          }

          const submitButton = form.querySelector('[type="submit"][name="add"], [type="submit"]');
          if (submitButton?.disabled) {
            console.warn("[HRC] Blocked add-to-cart: submit disabled.");
            return;
          }

          const wasDisabled = Boolean(submitButton?.disabled);
          submitButton?.setAttribute("aria-disabled", "true");
          submitButton?.setAttribute("disabled", "disabled");

          try {
            const res = await fetch(getAddJsUrl(form), {
              method: "POST",
              headers: { Accept: "application/json" },
              body: new FormData(form),
              credentials: "same-origin",
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              throw new Error(
                data?.description ||
                  data?.message ||
                  `Cart add failed: ${res.status}`,
              );
            }

            if (isUnauthorizedGiftItem(data)) {
              try {
                const key = data?.key;
                if (key) {
                  await fetch(`${getRoutesRoot()}cart/change.js`, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                      "Content-Type": "application/json",
                      Accept: "application/json",
                    },
                    body: JSON.stringify({ id: key, quantity: 0 }),
                  });
                }
              } catch (_) {
                /* ignore rollback errors */
              }
              console.warn("[HRC] Removed unauthorized gift-only line item after add.");
              return;
            }

            // List price 0 must never be sold; coupon → 0 final is allowed
            const listPrice = toCents(
              data?.original_price != null ? data.original_price : data?.price,
            );
            const addedVariantId = data?.variant_id ?? data?.id;
            if (
              !isCampaignGiftPayload(data) &&
              (listPrice === 0 ||
                (addedVariantId && isZeroPriceVariant(addedVariantId)))
            ) {
              // Roll back free list-price adds
              try {
                const key = data?.key;
                if (key) {
                  await fetch(`${getRoutesRoot()}cart/change.js`, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: {
                      "Content-Type": "application/json",
                      Accept: "application/json",
                    },
                    body: JSON.stringify({ id: key, quantity: 0 }),
                  });
                }
              } catch (_) {
                /* ignore rollback errors */
              }
              console.warn("[HRC] Removed 0 TL line item after add.");
              return;
            }

            await this.handleItemAdded(data);
            window.dispatchEvent(
              new CustomEvent("cart:updated", { detail: { lineItem: data } }),
            );
          } catch (err) {
            console.error("[HRC] Cart notification error:", err);
          } finally {
            if (submitButton && !wasDisabled && !formTargetsZeroPrice(form)) {
              submitButton.removeAttribute("aria-disabled");
              submitButton.removeAttribute("disabled");
            } else if (submitButton && (wasDisabled || formTargetsZeroPrice(form))) {
              submitButton.setAttribute("disabled", "disabled");
              submitButton.setAttribute("aria-disabled", "true");
            }
          }
        },
        true,
      );
    }

    appendCampaignGift(lineItem) {
      if (!this.productEl) return;
      if (!campaignGiftShouldShow()) return;
      if (this.productEl.querySelector("[data-hrc-campaign-gift-row]")) return;

      const item = campaignGiftFallbackItem(lineItem);
      const title = escapeHtml(item.product_title || item.title || "Hercules Mouthguard");
      const img = item?.featured_image?.url || item?.image || "";
      const imgAlt = escapeHtml(item?.featured_image?.alt || title);
      const zero = formatMoneyFromCents(0);
      const giftLabel = escapeHtml(
        window.hrcCampaignConfig?.copy?.giftCartLabel || "Kampanya Hediyesi",
      );
      const imageHtml = img
        ? `<img src="${escapeHtml(img)}" alt="${imgAlt}" loading="lazy" width="72" height="72" style="object-fit:cover;border-radius:8px;border:1px solid rgba(0,0,0,.08);" />`
        : "";

      const row = document.createElement("div");
      row.setAttribute("data-hrc-campaign-gift-row", "true");
      row.className = "hrc-cart-notification__gift";
      row.innerHTML = `
        <div style="display:flex;gap:14px;align-items:center;margin-top:1.2rem;padding-top:1.2rem;border-top:1px solid rgba(0,0,0,.08);">
          ${imageHtml}
          <div style="min-width:0;display:flex;flex-direction:column;gap:6px;">
            <div style="font-weight:700;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${title}</div>
            <div style="color:var(--hrc-color-accent);font-size:var(--hrc-fs-3xs);font-weight:600;letter-spacing:.12em;text-transform:uppercase;">${giftLabel}</div>
            <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:baseline;color:var(--hrc-text-primary);font-size:var(--hrc-fs-sm);">
              <span>Adet: 1</span>
              <span>${escapeHtml(zero)}</span>
            </div>
          </div>
        </div>
      `;
      this.productEl.appendChild(row);
    }

    async handleItemAdded(lineItem, { openNotification = true } = {}) {
      const addedQty = Number(lineItem?.quantity || 1);
      if (!isCampaignGiftItem(lineItem)) {
        updateCartCountUI(readCurrentCartCount() + addedQty);
      }

      if (openNotification) {
        this.renderProduct(lineItem);
        if (campaignGiftShouldShow() && !isCampaignGiftItem(lineItem)) {
          try {
            const cart = await fetchCart();
            const giftLine = (cart?.items || []).find((item) => isCampaignGiftItem(item));
            if (giftLine) this.appendCampaignGift(giftLine);
          } catch (_) {
            /* ignore */
          }
        }
        this.open();
      }

      syncCartFromServer()
        .then((cart) => {
          if (
            typeof publish === "function" &&
            typeof PUB_SUB_EVENTS !== "undefined"
          ) {
            const productVariantId =
              lineItem?.variant_id ??
              lineItem?.id ??
              lineItem?.items?.[0]?.variant_id ??
              lineItem?.items?.[0]?.id;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "hrc-cart-notification",
              cartData: cart,
              productVariantId,
            });
          }
        })
        .catch((err) => {
          console.error("[HRC] Cart fetch after add failed:", err);
        });
    }

    refreshFromCart(cart) {
      if (!this.isOpen || !this.lastVariantId || !Array.isArray(cart?.items)) return;
      const line = cart.items.find(
        (item) => Number(item.variant_id) === this.lastVariantId && !isCampaignGiftItem(item),
      );
      if (!line) return;
      this.renderProduct(line);
      const giftLine = cart.items.find((item) => isCampaignGiftItem(item));
      if (giftLine) this.appendCampaignGift(giftLine);
    }

    renderProduct(lineItem) {
      if (!this.productEl) return;
      this.lastVariantId = Number(lineItem?.variant_id || lineItem?.items?.[0]?.variant_id) || 0;

      const title = escapeHtml(
        lineItem?.product_title || lineItem?.title || "",
      );
      const variant = escapeHtml(lineItem?.variant_title || "");
      const quantity = Number(lineItem?.quantity || 1);
      const original = Number(lineItem?.original_price || lineItem?.price || 0);
      const final = Number(lineItem?.final_price ?? lineItem?.price ?? 0);
      const price = formatMoneyFromCents(final);
      const compare =
        original > final ? formatMoneyFromCents(original) : "";
      const percent =
        original > final && original > 0
          ? Math.round(((original - final) / original) * 100)
          : 0;
      const isB2B = window.hrcIsB2B === true;
      const badge =
        isB2B && percent >= 1
          ? `<span data-b2b-badge>Toptan %${percent} İndirim</span>`
          : "";

      const img = lineItem?.featured_image?.url || lineItem?.image || "";
      const imgAlt = escapeHtml(lineItem?.featured_image?.alt || title);

      const imageHtml = img
        ? `<img src="${escapeHtml(img)}" alt="${imgAlt}" loading="lazy" width="72" height="72" style="object-fit:cover;border-radius:8px;border:1px solid rgba(0,0,0,.08);" />`
        : "";

      this.productEl.innerHTML = `
        <div style="display:flex;gap:14px;align-items:center;">
          ${imageHtml}
          <div style="min-width:0;display:flex;flex-direction:column;gap:6px;">
            <div style="font-weight:700;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${title}</div>
            ${variant && variant !== "Default Title" ? `<div style="color:var(--hrc-text-muted);font-size:var(--hrc-fs-sm);line-height:1.2;">${variant}</div>` : ""}
            <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:baseline;color:var(--hrc-text-primary);font-size:var(--hrc-fs-sm);">
              <span>Adet: ${escapeHtml(quantity)}</span>
              ${compare ? `<s style="color:var(--hrc-text-muted);">${escapeHtml(compare)}</s>` : ""}
              <span>${escapeHtml(price)}</span>
              ${badge}
            </div>
          </div>
        </div>
      `;
    }

    open() {
      if (!this.root) return;
      if (this.isOpen) return;
      this.isOpen = true;

      this.lastActiveElement =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;

      this.root.classList.add("is-open");
      this.root.setAttribute("aria-hidden", "false");
      document.addEventListener("keydown", this.onKeydown);

      this.root.focus({ preventScroll: true });
    }

    close() {
      if (!this.root) return;
      if (!this.isOpen) return;
      this.isOpen = false;

      this.root.classList.remove("is-open");
      this.root.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", this.onKeydown);

      this.lastActiveElement?.focus?.({ preventScroll: true });
    }

    onClose() {
      this.close();
    }

    onContinue() {
      this.close();
    }

    onKeydown(e) {
      if (e.key === "Escape") this.close();
    }

    onBackdropClick(e) {
      if (!this.inner) return;
      if (e.target === this.root) this.close();
    }
  }

  const purgeUnauthorizedCartItems = async () => {
    try {
      const cart = await fetchCart();
      const freeItems = (cart?.items || []).filter((item) => {
        if (isCampaignGiftItem(item)) return false;
        return Number(item.original_price) === 0;
      });
      const giftAbuse = (cart?.items || []).filter((item) =>
        isUnauthorizedGiftItem(item),
      );
      const removable = [...freeItems, ...giftAbuse];
      if (!removable.length) return false;

      const seen = new Set();
      for (const item of removable) {
        if (!item.key || seen.has(item.key)) continue;
        seen.add(item.key);
        await fetch(`${getRoutesRoot()}cart/change.js`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }

      await syncCartFromServer();
      window.dispatchEvent(new CustomEvent("cart:updated"));
      return true;
    } catch (err) {
      console.error("[HRC] Failed to purge unauthorized cart items:", err);
      return false;
    }
  };

  let notificationInstance = null;

  const init = () => {
    if (window.__hrcCartNotificationInitialized) return;
    window.__hrcCartNotificationInitialized = true;
    notificationInstance = new HrcCartNotification();

    purgeUnauthorizedCartItems().then((removed) => {
      if (removed && /\/cart\/?$/.test(window.location.pathname)) {
        window.location.reload();
      }
    });

    window.HrcCartBadge = {
      setCount(count) {
        updateCartCountUI(count);
      },
      sync() {
        return syncCartFromServer();
      },
    };

    window.HrcCartNotify = {
      show(lineItem) {
        notificationInstance?.handleItemAdded(lineItem);
      },
      showGift(lineItem) {
        notificationInstance?.appendCampaignGift(lineItem);
      },
    };

    window.addEventListener("hrc:cart:item-added", (e) => {
      const lineItem = e.detail?.lineItem;
      if (lineItem) notificationInstance?.handleItemAdded(lineItem);
    });

    window.addEventListener("hrc:campaign:gift-added", (e) => {
      if (!notificationInstance?.isOpen) return;
      notificationInstance.appendCampaignGift(e.detail?.lineItem);
    });

    window.addEventListener("cart:updated", (e) => {
      notificationInstance?.refreshFromCart(e.detail?.cart);
      syncCartFromServer().catch((err) => {
        console.error("[HRC] Cart badge sync failed:", err);
      });
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
